import {
  Action,
  ActionType,
  FlowOperationType,
  FlowRun,
  FlowRunStatus,
  FlowVersion,
  Trigger,
  TriggerType,
  flowStructureUtil,
  isNil,
  isFlowControlStep,
} from '@activepieces/shared';
import { useDraggable } from '@dnd-kit/core';
import { Handle, NodeProps, Position } from '@xyflow/react';
import { t } from 'i18next';
import { ChevronDown, RouteOff } from 'lucide-react';
import React, { useMemo } from 'react';

import { StepStatusIcon } from '../../../../features/flow-runs/components/step-status-icon';
import { flowUtilConsts, STEP_CONTEXT_MENU_ATTRIBUTE } from '../utils/consts';
import { ApStepNode } from '../utils/types';

import { useBuilderStateContext } from '@/app/builder/builder-hooks';
import { PieceSelector } from '@/app/builder/pieces-selector';
import { InvalidStepIcon } from '@/components/custom/alert-icon';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { flowRunUtils } from '@/features/flow-runs/lib/flow-run-utils';
import { PieceIcon } from '@/features/pieces/components/piece-icon';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import { getPieceDisplayData } from '@/features/pieces/lib/piece-logos.generated';
import { cn } from '@/lib/utils';

function hasSkippedParent(stepName: string, flowVersion: FlowVersion): boolean {
  const step = flowStructureUtil.getStep(stepName, flowVersion);
  if (!step) {
    return false;
  }

  const skippedParents = flowStructureUtil
    .findPathToStep(step, flowVersion)
    .filter(
      (p) =>
        (p.type === ActionType.LOOP_ON_ITEMS || p.type === ActionType.ROUTER) &&
        flowStructureUtil.isChildOf(p, stepName) &&
        p.skip
    );
  return skippedParents.length > 0;
}

function getStepStatus(
  stepName: string | undefined,
  run: FlowRun | null,
  loopIndexes: Record<string, number>,
  flowVersion: FlowVersion
) {
  if (!run || !stepName || !run.steps) {
    return undefined;
  }
  const stepOutput = flowRunUtils.extractStepOutput(
    stepName,
    loopIndexes,
    run.steps,
    flowVersion
  );
  return stepOutput?.status;
}

const StepActionWrapper = React.memo(
  ({ children }: { children: React.ReactNode }) => {
    return (
      <div className="flex items-center gap-2 cursor-pointer">{children}</div>
    );
  }
);

StepActionWrapper.displayName = 'StepActionWrapper';
const ApStepCanvasNode = React.memo(
  ({ data }: NodeProps & Omit<ApStepNode, 'position'>) => {
    const [
      selectStepByName,
      isSelected,
      selectedStep,
      run,
      readonly,
      exitStepSettings,
      flowVersion,
      loopIndexes,
      setSelectedBranchIndex,
      setPieceSelectorStep,
      pieceSelectorStep,
    ] = useBuilderStateContext((state) => [
      state.selectStepByName,
      !isNil(state.selectedStep) && state.selectedStep === data.step?.name,
      state.selectedStep,
      state.run,
      state.readonly,
      state.exitStepSettings,
      state.flowVersion,
      state.loopsIndexes,
      state.setSelectedBranchIndex,
      state.setPieceSelectorStep,
      state.pieceSelectorStep,
    ]);
    const openPieceSelector = pieceSelectorStep === data.step!.name;
    const step =
      flowStructureUtil.getStep(data.step!.name, flowVersion) || data.step!;
    const { stepMetadata } = piecesHooks.useStepMetadata({
      step,
    });

    const pieceInfo = useMemo(() => {
      const pieceData = getPieceDisplayData(step.settings.pieceName);

      return {
        logoUrl: pieceData?.logoUrl || stepMetadata?.logoUrl,
        displayName: pieceData?.displayName || stepMetadata?.displayName,
      };
    }, [step.settings.pieceName, stepMetadata]);


    const stepIndex = useMemo(() => {
      const stepIndex = flowStructureUtil.findStepIndex(flowVersion, step.name);
      return stepIndex;
    }, [step.name, flowVersion]);

    const isTrigger = flowStructureUtil.isTrigger(step.type);
    const isAction = flowStructureUtil.isAction(step.type);

    const pieceSelectorOperation = isAction
      ? FlowOperationType.UPDATE_ACTION
      : FlowOperationType.UPDATE_TRIGGER;
    const isEmptyTriggerSelected =
      selectedStep === 'trigger' && step.type === TriggerType.EMPTY;
    const isSkipped = (step as Action).skip;

    const stepOutputStatus = useMemo(() => {
      return getStepStatus(step.name, run, loopIndexes, flowVersion);
    }, [step.name, run, loopIndexes, flowVersion]);

    const showRunningIcon =
      isNil(stepOutputStatus) &&
      run?.status === FlowRunStatus.RUNNING &&
      !hasSkippedParent(step.name, flowVersion) &&
      !isSkipped;
      
    const handleStepClick = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      const { name } = data.step!;
      selectStepByName(name);
      setSelectedBranchIndex(null);
      e.preventDefault();
      e.stopPropagation();
    };
    return (
      <div
        {...{
          [`data-${STEP_CONTEXT_MENU_ATTRIBUTE}`]: !isFlowControlStep(
            step.settings.pieceName
          )
            ? step.name
            : undefined,
        }}
        style={{
          height: `${flowUtilConsts.AP_NODE_SIZE.STEP.width}px`,
          width: `${flowUtilConsts.AP_NODE_SIZE.STEP.width}px`,
        }}
        className={cn(
          'transition-all border-box rounded-sm border border-solid border-border relative group shadow-step-container bg-background rounded-full',
          {
            'border-primary': isSelected,
            'bg-accent/90': isSkipped,
          }
        )}
        onClick={handleStepClick}
        key={step.name}
      >
        <div className="absolute bottom-1 left-0 z-10">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
            {stepIndex}
          </div>
        </div>
        <div className="absolute top-1 left-6 flex items-center gap-1">
          {stepOutputStatus && (
            <StepStatusIcon status={stepOutputStatus} size="4" />
          )}
          {showRunningIcon && <LoadingSpinner className="w-4 h-4" />}
          {isSkipped && (
            <Tooltip>
              <TooltipTrigger asChild>
                <RouteOff className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('Skipped')}</TooltipContent>
            </Tooltip>
          )}
          {!step.valid && !isSkipped && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mr-3">
                  <InvalidStepIcon
                    size={16}
                    viewBox="0 0 16 15"
                    className="stroke-0 animate-fade"
                  ></InvalidStepIcon>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('Incomplete settings')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {!readonly && !isFlowControlStep(step.settings.pieceName) && (
          <div className="absolute top-0 right-2 p-1">
            <Button
              variant="ghost"
              size="sm"
              className="p-1 size-7"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (e.target) {
                  const rightClickEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.target.dispatchEvent(rightClickEvent);
                }
              }}
            >
              <ChevronDown className="w-4 h-4 stroke-muted-foreground" />
            </Button>
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={isSkipped ? 'opacity-80' : ''}>
            <PieceIcon
              logoUrl={pieceInfo?.logoUrl}
              displayName={pieceInfo?.displayName}
              showTooltip={false}
              size="xl"
            />
          </div>
        </div>

        <div className="absolute pointer-events-none text-accent-foreground text-sm transition-all duration-300 text-center top-[calc(100%+4px)] left-1/2 -translate-x-1/2 w-[200px]">
          <div>
            {pieceInfo?.displayName === 'Empty Trigger'
              ? t('Empty Trigger')
              : pieceInfo?.displayName}
          </div>
          <div className="text-xs text-muted-foreground">
            {step.displayName === 'Select Trigger'
              ? t('Select Trigger')
              : t(step.displayName, {
                  ns: step.settings.pieceName?.replace('@activepieces/', '')
                })}
          </div>
        </div>

        <PieceSelector
          initialSelectedPiece={
            step.type === TriggerType.EMPTY
              ? undefined
              : stepMetadata?.displayName
          }
          operation={{
            type: isEmptyTriggerSelected
              ? FlowOperationType.UPDATE_TRIGGER
              : pieceSelectorOperation,
            stepName: step.name,
          }}
          open={openPieceSelector || isEmptyTriggerSelected}
          onOpenChange={(open) => {
            setPieceSelectorStep(open ? step.name : null);
            if (!open && step.type === TriggerType.EMPTY) {
              exitStepSettings();
            }
          }}
          asChild={true}
        >
          <div
            className="h-full w-full"
            onClick={(e) => {
              if (!openPieceSelector) {
                handleStepClick(e);
              }
            }}
          />
        </PieceSelector>

        <Handle
          type="source"
          style={flowUtilConsts.HANDLE_STYLING}
          position={Position.Right}
        />
        <Handle
          type="target"
          style={{
            opacity: 0,
            cursor: 'default',
            width: 0,
            height: 0,
          }}
          position={Position.Left}
        />
      </div>
    );
  }
);

ApStepCanvasNode.displayName = 'ApStepCanvasNode';
export { ApStepCanvasNode };
